import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Store scroll positions for each path
const scrollPositions = new Map();

export const ScrollRestoration = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevPathRef = useRef(location.pathname);
  const scrollRAF = useRef(null);

  useEffect(() => {
    // On location change
    if (prevPathRef.current !== location.pathname) {
      // Save previous page scroll position
      scrollPositions.set(prevPathRef.current, window.scrollY);
      prevPathRef.current = location.pathname;
    }

    // Determine scroll behavior based on navigation type
    if (navigationType === "POP") {
      // Going back/forward - restore scroll position
      const savedPosition = scrollPositions.get(location.pathname);
      if (savedPosition !== undefined) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo(0, savedPosition);
        });
      }
    } else {
      // PUSH or REPLACE - new navigation, scroll to top
      window.scrollTo(0, 0);
    }

    return () => {
      // Save scroll position when component unmounts or location changes
      scrollPositions.set(location.pathname, window.scrollY);
    };
  }, [location.pathname, navigationType]);

  // Throttled scroll handler (250ms) - better for performance
  useEffect(() => {
    let lastScrollTime = 0;
    const throttleMs = 250;
    
    const handleScroll = () => {
      const now = Date.now();
      if (now - lastScrollTime < throttleMs) {
        // Cancel any pending RAF and schedule a new one
        if (scrollRAF.current) {
          cancelAnimationFrame(scrollRAF.current);
        }
        scrollRAF.current = requestAnimationFrame(() => {
          scrollPositions.set(location.pathname, window.scrollY);
          lastScrollTime = Date.now();
        });
        return;
      }
      
      scrollPositions.set(location.pathname, window.scrollY);
      lastScrollTime = now;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (scrollRAF.current) {
        cancelAnimationFrame(scrollRAF.current);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location.pathname]);

  return null;
};
